/**
 * Factions.js
 *
 * Phase A (June 2026) — Galactic Factions & Reputation dashboard at /factions.
 * Shows the three factions of KRAKEN, the player's reputation + tier with each,
 * tier perks, and how to earn (or lose) standing.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Flag, Pickaxe, Shield, Skull, ArrowLeft, TrendingUp, TrendingDown } from 'lucide-react';

const FACTION_ICONS = { vmc: Pickaxe, unc: Shield, crimson: Skull };

const TIER_STOPS = [
  { tier: 'hostile', label: 'Hostile', at: -100 },
  { tier: 'neutral', label: 'Neutral', at: 0 },
  { tier: 'friendly', label: 'Friendly', at: 50 },
  { tier: 'honored', label: 'Honored', at: 200 },
];

const EARN_TIPS = {
  vmc: [
    '+ Complete contracts in VMC-controlled systems',
    '+ Sell ore at VMC cities & stations',
    '− Completing Crimson Pact contracts',
  ],
  unc: [
    '+ Destroy pirate marauder ships in space',
    '+ Complete contracts in UNC-controlled systems',
    '− Working for the Crimson Pact',
  ],
  crimson: [
    '+ Complete contracts in Pact-controlled systems',
    '+ Sell ore at Pact havens',
    '− Destroying marauder ships (the Pact remembers)',
  ],
};

function repToPercent(rep) {
  // Map [-300 .. 300] to a 0..100 progress track (clamped) for display.
  const clamped = Math.max(-300, Math.min(300, rep));
  return ((clamped + 300) / 600) * 100;
}

export default function Factions() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get('/api/factions');
      setData(d);
    } catch (_e) {
      setData({ factions: [] });
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const factions = data?.factions || [];

  return (
    <div className="min-h-screen">
      <Header breadcrumbs={[{ label: 'Factions' }]} />
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Flag className="w-7 h-7 text-cyan-300" />
            <div>
              <h1 className="text-3xl font-bold text-primary" data-testid="factions-title">Galactic Factions</h1>
              <p className="text-sm text-muted-foreground">
                Three powers carve up KRAKEN. Your reputation with each decides who trades with you — and who shoots first.
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate('/explore/octants')} data-testid="factions-back">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Explore
          </Button>
        </div>

        {data === null ? (
          <div className="flex justify-center py-20"><div className="spinner" /></div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            {factions.map((f) => {
              const Icon = FACTION_ICONS[f.id] || Flag;
              const pct = repToPercent(f.rep);
              return (
                <div
                  key={f.id}
                  className="glass-card rounded-xl p-6 border"
                  style={{ borderColor: `${f.color}55`, boxShadow: `0 0 30px ${f.color}18` }}
                  data-testid={`faction-card-${f.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-11 h-11 rounded-lg flex items-center justify-center"
                        style={{ background: `${f.color}22`, border: `1px solid ${f.color}66` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: f.color }} />
                      </div>
                      <div>
                        <div className="font-bold text-lg leading-tight" style={{ color: f.color }}>{f.name}</div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{f.role}</div>
                      </div>
                    </div>
                    <Badge
                      variant="outline"
                      className="font-semibold uppercase tracking-wider"
                      style={{ borderColor: `${f.color}66`, color: f.color }}
                      data-testid={`faction-tier-${f.id}`}
                    >
                      {f.tier_label}
                    </Badge>
                  </div>

                  <p className="text-xs italic mt-3" style={{ color: `${f.color}cc` }}>“{f.motto}”</p>
                  <p className="text-sm text-slate-300 mt-2 leading-relaxed">{f.blurb}</p>

                  {/* Rep track */}
                  <div className="mt-5">
                    <div className="flex items-center justify-between text-xs text-slate-400 mb-1">
                      <span>Reputation</span>
                      <span className="font-mono tabular-nums" data-testid={`faction-rep-${f.id}`}>{f.rep}</span>
                    </div>
                    <div className="relative h-3 rounded-full bg-slate-900/80 border border-slate-700/60 overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0 transition-all"
                        style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${f.color}33, ${f.color})` }}
                      />
                      {/* tier stop ticks */}
                      {TIER_STOPS.map((s) => (
                        <div
                          key={s.tier}
                          className="absolute inset-y-0 w-px bg-slate-500/60"
                          style={{ left: `${repToPercent(s.at)}%` }}
                          title={`${s.label} at ${s.at}`}
                        />
                      ))}
                    </div>
                    <div className="flex justify-between text-[10px] text-slate-500 mt-1 uppercase tracking-wider">
                      <span>Hostile</span><span>Neutral</span><span>Friendly</span><span>Honored</span>
                    </div>
                  </div>

                  {/* Perks at current tier */}
                  <div className="mt-4 rounded-lg border border-slate-700/50 bg-black/30 p-3">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400 mb-2">Current Perks</div>
                    {f.perks?.refused ? (
                      <div className="text-sm text-red-400 flex items-center gap-2">
                        <TrendingDown className="w-4 h-4" /> Service refused at their colonies & stations
                      </div>
                    ) : (
                      <div className="space-y-1 text-sm text-slate-300">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" style={{ color: f.color }} />
                          {Math.round((f.perks?.buy_discount || 0) * 100)}% shop & refit discount
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" style={{ color: f.color }} />
                          +{Math.round((f.perks?.sell_bonus || 0) * 100)}% ore sell bonus
                        </div>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4" style={{ color: f.color }} />
                          {f.tier === 'friendly' || f.tier === 'honored'
                            ? 'Exclusive Strike Order contracts unlocked'
                            : 'Reach Friendly (50) for exclusive contracts'}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* How to earn */}
                  <div className="mt-3 text-xs text-slate-400 space-y-1">
                    {(EARN_TIPS[f.id] || []).map((t) => (
                      <div key={t} className={t.startsWith('−') ? 'text-red-400/80' : 'text-emerald-300/80'}>{t}</div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
